import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useSearchParams } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import { useVendorPayments, useSupplyVendorsDropdown, useBankAccountsDropdown } from "../../hooks/useInventory";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";

import formatDate from "../../components/utils/DateFormatter";
const VendorPaymentList = () => {
  const [searchParams] = useSearchParams();
  const presetVendorId = searchParams.get("vendor_id") || "";

  const [search, setSearch] = useState("");
  const [vendorId, setVendorId] = useState(presetVendorId);
  const [bankId, setBankId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useVendorPayments({ search, supply_vendor_id: vendorId, company_bank_account_id: bankId, start_date: startDate, end_date: endDate, page, per_page: 15 });
  const { data: vendorsData } = useSupplyVendorsDropdown();
  const { data: banksData } = useBankAccountsDropdown();
  const payments = data?.data?.payments || [];
  const summary = data?.data?.summary || {};
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / 15);

  const vendors = vendorsData?.data || [];
  const banks = banksData?.data || [];

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return formatDate(dateStr);
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Vendor Payments</h1>
          <Link to="/head-office/inventory/vendor-payments/create" className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition">
            + Record Payment
          </Link>
        </div>

        {/* Summary cards */}
        {summary.total_payments != null && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {[
              { label: "Total Amount Paid", value: parseFloat((summary.total_amount || "0").toString().replace(/,/g, "")) },
              { label: "Payment Count", count: summary.total_payments },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-lg shadow p-4">
                <p className="text-gray-500 text-sm">{card.label}</p>
                <p className="text-xl font-bold mt-1">
                  {card.count !== undefined ? card.count : <CurrencyFormatter amount={card.value} />}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3">
          <input type="text" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1 flex-1 min-w-[160px]" />
          <select value={vendorId} onChange={(e) => { setVendorId(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1">
            <option value="">All Vendors</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select value={bankId} onChange={(e) => { setBankId(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1">
            <option value="">All Banks</option>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} - {b.account_name}</option>)}
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
        </div>

        {isLoading ? (
          <Spinner loading />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Date", "Reference", "Vendor", "Amount", "Action"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        No payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{formatDateTime(p.payment_date)}</td>
                        <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{p.payment_reference}</td>
                        <td className="px-5 py-4 text-sm font-medium text-gray-900">{p.vendor?.name || "—"}</td>
                        <td className="px-5 py-4 text-sm font-semibold text-green-700">
                          <CurrencyFormatter amount={parseFloat((p.amount || "0").toString().replace(/,/g, ""))} />
                        </td>
                        <td className="px-5 py-4 text-sm font-medium whitespace-nowrap">
                          <Link to={`/head-office/inventory/vendor-payments/show?id=${p.id}`} className="text-orange1 hover:text-orange-600" title="View Details">
                            <Eye width={18} height={18} />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <p className="text-sm text-gray-600">Total: {total}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <span className="px-3 py-1 text-sm">{page} / {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default VendorPaymentList;
